import { useEffect, useState } from "react";
import { honeyStockApi } from "../../services/businessApi";
import { Card } from "../common/Card";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { VarietalManager } from "./VarietalManager";

const MUTED = { color: "var(--color-text-muted)" };

/**
 * The pool split by varietal, and the list behind it (#225, #410).
 *
 * Business tier only, which is the whole distinction: a Harvester account
 * still HAS these rows -- every tier writes a varietal -- they simply see
 * one pool, which is the honest picture for someone who never separated
 * them.
 *
 * ── ONE CARD, TWO FACES ──
 *
 * Asked for after testing on the web app:
 *
 *     "the Your Varietals dropdown is good but I think it should be
 *      hidden and put a 'Manage' button to the right of the 'By Varietal'
 *      label... I like the 'Add' button to the right of the label like in
 *      the 'Bottle Sizes' card but the 'Rename' and 'Put Away'/'Bring
 *      Back' Buttons beside each entry... and a Done button at the bottom
 *      that hides it back"
 *
 * The manage table used to sit permanently below this one, which put the
 * varietal list on the page TWICE -- once as the numbers people came for,
 * once as the same names with buttons beside them. Curating the list is
 * the rarest gesture on the page: you do it once, after a typo.
 *
 * So the header carries the action and the body swaps. Managing shows
 * Add where Manage was, because those are the same corner and only one
 * of them is useful at a time -- and the way back out is Done at the
 * bottom of the list, where you are once you have finished reading it.
 *
 * ── IT RENDERS EVEN WITH ONE VARIETAL, WHICH IT DID NOT BEFORE ──
 *
 * The old rule was "a breakdown of a single row is not a breakdown", and
 * that is still true of the TABLE. But the Manage button lives on this
 * card, so keeping the whole card hidden until a second varietal existed
 * would mean a Business account with one varietal had nowhere to add a
 * second from -- the list could only be grown from the "+ New" on a
 * picker inside a form somebody had already started filling in. That is
 * the dead end #358 and #398 were both about, arrived at from a new
 * direction.
 *
 * So with one varietal the card says so in a line instead of repeating
 * the pool total under a second heading.
 */
/* canChangeLists comes from the parent (#509) so this stays renderable
   without the theme provider; undefined means allowed. */
export function VarietalBreakdown({ poolPounds, onChanged, canChangeLists }) {
  const [rows, setRows] = useState(null);
  const [managing, setManaging] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    honeyStockApi
      .byVarietal()
      .then((r) => !cancelled && setRows(r.by_varietal || []))
      // Best-effort, like the vendor balances: a breakdown failing must not
      // take down the pool page it sits beside.
      .catch(() => !cancelled && setRows([]));
    return () => { cancelled = true; };
  }, [poolPounds]);

  // Still loading. Showing a card with an empty body would flash a
  // "nothing here yet" at somebody who has plenty.
  if (!rows) return null;

  return (
    <Card
      title="By varietal"
      className="section-spacing"
      actions={canChangeLists === false ? null : managing
        ? <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>Add</Button>
        : <Button size="sm" variant="secondary" onClick={() => setManaging(true)}>Manage</Button>}
    >
      {managing ? (
        <VarietalManager
          adding={adding}
          onAddingClose={() => setAdding(false)}
          // The pickers on the page around this are holding the old list.
          onChanged={onChanged}
          onClose={() => setManaging(false)}
        />
      ) : rows.length < 2 ? (
        /* A breakdown of one row is not a breakdown, and repeating the
           pool total under a second heading invites the reader to think
           the two numbers mean different things. */
        <p style={{ margin: 0, ...MUTED }}>
          All your honey is recorded as one varietal so far. Add another and
          this splits the pool between them.
        </p>
      ) : (
        <DataTable
          rows={rows}
          rowKey="varietal_id"
          columns={[
            { key: "varietal_name", header: "Varietal" },
            {
              key: "pounds",
              header: "In the pool",
              render: (r) => `${Number(r.pounds).toFixed(1)} lb`,
            },
          ]}
        />
      )}
    </Card>
  );
}
